import { Alert, Anchor, Container, Grid, LoadingOverlay, Stack, Text } from '@mantine/core'
import { IconAlertCircle, IconBuildingWarehouse } from '@tabler/icons'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React from 'react'

import { Heading, PageCard } from '@/components/surfaces'
import { useFetch } from '@/lib/hooks'
import { getSession } from '@/lib/utils'
import {
  Channel as ChannelType,
  Showcase as ShowcaseType,
  Site as SiteType,
  Store as StoreType
} from '@/types'

interface Data {
  site?: SiteType
  channel?: ChannelType
  store?: StoreType
}

interface Props {
  initialData?: Data
}

export default function StoreView({ initialData }: Props) {
  // Hooks
  const router = useRouter()

  // Constants
  const { site: siteSlug, channel: channelSlug, store: storeSlug } = router.query || {}
  const sessionStudentUid = getSession('stuid')

  // Fetch
  const { data: siteData } = useFetch([siteSlug ? `/${siteSlug}/` : null], {
    fallbackData: initialData?.site // rehydration if necessary
  })

  const isSitePrivate = siteData?.privacy === 'private'

  const { data: storeData } = useFetch(
    [
      siteSlug && channelSlug && storeSlug
        ? `/${siteSlug}/channels/${channelSlug}/stores/${storeSlug}/`
        : null
    ],
    { fallbackData: initialData?.store } // rehydration if necessary
  )

  const { data: showcasesData, error: showcasesDataError } = useFetch([
    siteSlug && channelSlug && storeSlug && siteData
      ? `/${siteSlug}/channels/${channelSlug}/stores/${storeSlug}/showcases/`
      : null,
    isSitePrivate ? { student_site_profile: sessionStudentUid || '' } : {}
  ])

  const isLoading = !showcasesData && !showcasesDataError

  return (
    <Container size="xl" py="xl">
      <LoadingOverlay visible={isLoading} />

      <Heading
        title={
          storeData ? `${storeData?.grade?.parent?.name}: ${storeData?.displayName}` : undefined
        }
        description="Selecione uma vitrine:"
        icon={<IconBuildingWarehouse size={48} />}
      />

      {(showcasesDataError || !showcasesData?.results?.length) && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Erro de configuração"
          color="orange"
          mb="md"
          hidden={isLoading}>
          <Text>
            Houve um erro de configuração das vitrines. Entre em contato no{' '}
            <Anchor size="sm" href="/help" component={Link}>
              Atendimento
            </Anchor>
            .
          </Text>
        </Alert>
      )}
      <Stack>
        {Array.isArray(showcasesData?.results) && !!showcasesData?.results?.length && (
          <Grid gutter="lg">
            {showcasesData?.results.map((showcase: ShowcaseType) => (
              <Grid.Col key={showcase?.uid} span={12} xs={6} md={4} lg={3}>
                <PageCard
                  title={showcase?.displayName + ' ' + showcase?.cycle}
                  slug={showcase?.slug}
                  image={showcase?.image}
                />
              </Grid.Col>
            ))}
          </Grid>
        )}
      </Stack>
    </Container>
  )
}
